<template>
  <div>
    <h4 class="title ml-3">
      {{ $t('box.efficiency') + $t('dashboards.for') + tabPeriodText }}
      <i id="efficiencyInfo" class="mdi mdi-24px mdi-info mdi-information-outline"></i>
    </h4>
    <b-tooltip
      custom-class="info-tooltip"
      target="efficiencyInfo"
      variant="danger"
      :title="$t('boxInfo.tooltip.efficiency')"
    ></b-tooltip>
    <apexchart width="99%" height="400" :options="options" :series="series"></apexchart>
  </div>
</template>

<script>
import workCenterEfficiency from '@/api/boxinfo/workCenterEfficiency.gql';
import moment from 'moment';
import ru from 'apexcharts/dist/locales/ru.json';

export default {
  name: 'BoxEfficiencyPlot',
  components: {
    apexchart: () => import('vue-apexcharts'),
  },
  props: {
    workCenterId: String,
    period: Object,
    targetEfficiency: Number,
    tabPeriodText: String,
  },
  apollo: {
    efficiencyValues: {
      query() {
        return this.query;
      },
      variables() {
        return {
          workCenterId: this.workCenterId,
          period: this.period,
        };
      },
      update(data) {
        const queryData = data[Object.keys(data)[0]];
        if (queryData && queryData.overallEfficiency) {
          return queryData.overallEfficiency;
        } else {
          //this.$store.commit('SET_ERROR', this.$t('box.error.efficiency'))
          return [];
        }
      },
      skip() {
        return !this.workCenterId || !this.period;
      },
      error(e) {
        this.$store.commit('SET_ERROR', e.message);
      },
    },
  },
  data: () => {
    return {
      query: workCenterEfficiency,
      efficiencyValues: [],
    };
  },
  computed: {
    isiOS() {
      return !!navigator.platform && /Mac|iPad|iPhone|iPod/.test(navigator.platform);
    },
    series() {
      const ef = this.efficiencyValues;
      const isIOS = this.isiOS;
      const series = [
        {
          name: this.$t('box.efficiency'),
          type: 'bar',
          data: ef.map((i) => {
            return {
              x: this.dateToTimestamp(i.dateFrom, isIOS),
              y: i.oee < 3 ? 3 : i.oee,
            };
          }),
        },
      ];
      if (ef && ef.length) {
        const target = this.targetEfficiency;
        const dates = ef.map((i) => moment(i.dateFrom));
        const targetEfficiencyData = [
          { x: moment.min(dates), y: target },
          { x: moment.max(dates), y: target },
        ];
        series.push({
          name: this.$t('box.targetEfficiency'),
          type: 'line',
          data: targetEfficiencyData,
        });
      }

      return series;
    },
    options() {
      const vm = this;
      const isIOS = this.isiOS;
      const target = this.targetEfficiency;
      return {
        chart: {
          id: 'workCenterEfficiency',
          group: 'efficiency',
          height: 350,
          type: 'bar',
          animations: {
            enabled: false,
          },
          toolbar: {
            show: true,
            tools: {
              download: false,
              selection: false,
              zoom: true,
              zoomin: false,
              zoomout: false,
              pan: false,
              reset: true,
            },
          },
          locales: [ru],
          defaultLocale: 'ru',
        },
        plotOptions: {
          bar: {
            columnWidth: '80%',
          },
        },
        colors: [
          function ({ value }) {
            return value == 3
              ? '#9b0000'
              : value < target * 0.7
              ? '#FF0000'
              : target * 0.7 <= value && value < target
              ? '#FC9A40'
              : '#008736';
          },
        ],
        grid: {
          borderColor: 'rgba(0,0,0,1)',
          xaxis: {
            lines: {
              show: true,
            },
          },
          yaxis: {
            lines: {
              show: true,
            },
          },
        },
        stroke: {
          width: 2,
        },
        //fill: { opacity: 0.5,  },
        tooltip: {
          theme: 'dark',
          x: {
            formatter: function (value) {
              return isIOS
                ? moment(value).add(3, 'h').format('DD.MM.YYYY HH:mm:ss')
                : moment(value).format('DD.MM.YYYY HH:mm:ss');
            },
          },
          enabled: true,
          y: {
            formatter: function (value, { dataPointIndex }) {
              return value == 3 && vm.efficiencyValues.length > dataPointIndex
                ? vm.efficiencyValues[dataPointIndex].oee
                : value;
            },
          },
        },
        legend: {
          show: false,
        },
        xaxis: {
          type: 'datetime',
          axisBorder: {
            show: true,
          },
          axisTicks: {
            show: true,
          },
          labels: {
            datetimeUTC: false,
            style: {
              colors: 'grey',
            },
          },
        },
        yaxis: {
          min: 0,
          max: 120,
          tickAmount: 6,
          axisBorder: {
            show: false,
          },
          axisTicks: {
            show: false,
          },
          labels: {
            show: true,
            minWidth: 40,
            formatter: function (val) {
              return val + '%';
            },
            style: {
              colors: 'grey',
            },
          },
        },
        dataLabels: {
          enabled: false,
        },
      };
    },
    loading() {
      return this.$apollo.queries.efficiencyValues.loading;
    },
  },
  watch: {
    period() {
      this.selectionInner = null;
    },
  },
  methods: {
    dateToTimestamp(date, isiOS) {
      return isiOS ? moment(date).add(-3, 'h').valueOf() : moment(date).valueOf();
    },
  },
};
</script>
